import { Modal } from "react-bootstrap";
import { useEffect, useState } from "react";
import { IFood } from "../models/IFood";
import { FoodDiscountType, IFoodDiscount } from "../models/IFoodDiscount";
import {
  createFoodDiscount,
  deleteFoodDiscount,
  getFoodDiscountsByFoodId,
  toggleFoodDiscountActive,
  updateFoodDiscount,
} from "../services/foodDiscount";

interface Props {
  food: IFood | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const emptyForm = {
  discountType: "Percentage" as FoodDiscountType,
  discountValue: "",
  startsAtUtc: "",
  endsAtUtc: "",
  description: "",
};

function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function toLocalInput(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function valueLabel(discountType: FoodDiscountType, discountValue: number): string {
  return discountType === "Percentage" ? `%${discountValue}` : `${discountValue} TL`;
}

function FoodDiscountPopup({ food, isOpen, onClose, onSaved }: Props) {
  const [discounts, setDiscounts] = useState<IFoodDiscount[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);

  const loadDiscounts = (foodId: string) => {
    setLoading(true);
    setLoadError("");
    getFoodDiscountsByFoodId(foodId)
      .then((res) => setDiscounts(res.data ?? []))
      .catch(() => setLoadError("Kampanyalar yüklenemedi."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isOpen && food) {
      loadDiscounts(food.id);
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      setFormError("");
      setRowError(null);
      setConfirmDeleteId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, food?.id]);

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError("");
    setShowForm(true);
  };

  const startEdit = (d: IFoodDiscount) => {
    setEditingId(d.id);
    setForm({
      discountType: d.discountType,
      discountValue: String(d.discountValue),
      startsAtUtc: toLocalInput(d.startsAtUtc),
      endsAtUtc: toLocalInput(d.endsAtUtc),
      description: d.description ?? "",
    });
    setFormError("");
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!food) return;
    const value = Number(form.discountValue);
    if (!form.discountValue || isNaN(value) || value <= 0) {
      setFormError("Geçerli bir indirim değeri girin.");
      return;
    }
    setSaving(true);
    setFormError("");

    const payload = {
      discountType: form.discountType,
      discountValue: value,
      startsAtUtc: form.startsAtUtc ? new Date(form.startsAtUtc).toISOString() : undefined,
      endsAtUtc: form.endsAtUtc ? new Date(form.endsAtUtc).toISOString() : undefined,
      description: form.description.trim() || undefined,
    };

    const req = editingId
      ? updateFoodDiscount(editingId, payload)
      : createFoodDiscount({ foodId: food.id, ...payload });

    req
      .then(() => {
        setShowForm(false);
        loadDiscounts(food.id);
        onSaved();
      })
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description;
        setFormError(desc ?? "Kampanya kaydedilemedi.");
      })
      .finally(() => setSaving(false));
  };

  const handleToggle = (d: IFoodDiscount) => {
    if (!food) return;
    setTogglingId(d.id);
    setRowError(null);
    toggleFoodDiscountActive(d.id)
      .then(() => { loadDiscounts(food.id); onSaved(); })
      .catch(() => setRowError({ id: d.id, message: "Durum değiştirilemedi." }))
      .finally(() => setTogglingId(null));
  };

  const handleDelete = (id: string) => {
    if (!food) return;
    setDeletingId(id);
    setRowError(null);
    deleteFoodDiscount(id)
      .then(() => { setConfirmDeleteId(null); loadDiscounts(food.id); onSaved(); })
      .catch((err) => {
        const desc = err?.response?.data?.[0]?.description;
        setRowError({ id, message: desc ?? "Kampanya silinemedi." });
        setConfirmDeleteId(null);
      })
      .finally(() => setDeletingId(null));
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered scrollable>
      <Modal.Header closeButton style={{ background: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
        <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700 }}>
          <i className="bi bi-percent me-2" style={{ color: "#b45309" }}></i>
          Kampanyalar{food ? ` — ${food.foodNameTr}` : ""}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {food && (
          <div className="mb-3 d-flex align-items-center gap-2 small text-muted">
            <span>Liste fiyatı:</span>
            <strong className="text-dark">{food.foodPrice} TL</strong>
          </div>
        )}

        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-warning" /></div>
        )}
        {loadError && <div className="alert alert-danger py-2 small">{loadError}</div>}

        {!loading && !loadError && discounts.length === 0 && (
          <div className="text-center text-muted py-3 small">
            <i className="bi bi-percent d-block mb-2" style={{ fontSize: "1.8rem", opacity: 0.3 }}></i>
            Bu üründe hiç kampanya tanımlanmamış.
          </div>
        )}

        {!loading && discounts.length > 0 && (
          <div className="d-flex flex-column gap-2 mb-3">
            {discounts.map((d) => (
              <div key={d.id} className="border rounded-3 p-2 px-3"
                style={{ background: d.isInEffect ? "#fffbeb" : "#fff", borderColor: d.isInEffect ? "#fde68a" : "#e5e7eb" }}>
                <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap">
                  <div>
                    <div className="fw-bold" style={{ color: "#b45309" }}>
                      {valueLabel(d.discountType, d.discountValue)}
                      <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.8rem" }}>
                        <s>{d.listPrice} TL</s> → <strong className="text-dark">{d.discountedPrice} TL</strong>
                      </span>
                    </div>
                    {d.description && <div className="small text-muted mt-1">{d.description}</div>}
                    <div className="small text-muted mt-1">
                      <i className="bi bi-calendar3 me-1"></i>
                      {formatDateTime(d.startsAtUtc)} → {d.endsAtUtc ? formatDateTime(d.endsAtUtc) : "süresiz"}
                      {!d.endsAtUtc && (
                        <i className="bi bi-exclamation-triangle-fill text-warning ms-2" title="Bitiş tarihi yok — elle kapatılana kadar sürer"></i>
                      )}
                    </div>
                  </div>
                  <div className="d-flex flex-column align-items-end gap-1">
                    <div className="d-flex gap-1">
                      {d.isInEffect && <span className="badge bg-warning text-dark">Şu an geçerli</span>}
                      <span className={`badge ${d.isActive ? "bg-success" : "bg-secondary"}`}>{d.isActive ? "Aktif" : "Pasif"}</span>
                    </div>
                    <div className="d-flex gap-1 mt-1">
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => startEdit(d)} title="Düzenle">
                        <i className="bi bi-pencil-fill"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-warning" onClick={() => handleToggle(d)} disabled={togglingId === d.id}
                        title={d.isActive ? "Pasife al" : "Aktifleştir"}>
                        {togglingId === d.id
                          ? <span className="spinner-border spinner-border-sm" />
                          : <i className={`bi ${d.isActive ? "bi-toggle-on" : "bi-toggle-off"}`}></i>}
                      </button>
                      {confirmDeleteId === d.id ? (
                        <>
                          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(d.id)} disabled={deletingId === d.id}>
                            {deletingId === d.id ? <span className="spinner-border spinner-border-sm" /> : "Sil"}
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmDeleteId(null)}>Vazgeç</button>
                        </>
                      ) : (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmDeleteId(d.id)} title="Sil">
                          <i className="bi bi-trash-fill"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {rowError?.id === d.id && (
                  <div className="text-danger small mt-2"><i className="bi bi-exclamation-circle me-1"></i>{rowError.message}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {!showForm ? (
          <button className="btn btn-warning text-white w-100" style={{ borderRadius: 10, fontWeight: 600 }} onClick={startCreate}>
            <i className="bi bi-plus-lg me-1"></i>Yeni Kampanya
          </button>
        ) : (
          <div className="border rounded-3 p-3" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong style={{ color: "#b45309" }}>{editingId ? "Kampanyayı Düzenle" : "Yeni Kampanya"}</strong>
              <button className="btn-close" onClick={() => setShowForm(false)}></button>
            </div>
            <div className="row g-2">
              <div className="col-6">
                <label className="form-label small fw-semibold">İndirim Türü</label>
                <select className="form-select form-select-sm" value={form.discountType}
                  onChange={(e) => setForm((p) => ({ ...p, discountType: e.target.value as FoodDiscountType }))}>
                  <option value="Percentage">Yüzde (%)</option>
                  <option value="FixedAmount">Tutar (TL)</option>
                </select>
              </div>
              <div className="col-6">
                <label className="form-label small fw-semibold">
                  {form.discountType === "Percentage" ? "Yüzde (0-100) *" : "İndirim Tutarı (TL) *"}
                </label>
                <input
                  type="number" min="0" max={form.discountType === "Percentage" ? 100 : undefined} step="0.01"
                  className="form-control form-control-sm" value={form.discountValue}
                  onChange={(e) => setForm((p) => ({ ...p, discountValue: e.target.value }))}
                />
              </div>
              <div className="col-6">
                <label className="form-label small fw-semibold">Başlangıç <span className="text-muted fw-normal">(boş = hemen)</span></label>
                <input type="datetime-local" className="form-control form-control-sm" value={form.startsAtUtc}
                  onChange={(e) => setForm((p) => ({ ...p, startsAtUtc: e.target.value }))} />
              </div>
              <div className="col-6">
                <label className="form-label small fw-semibold">Bitiş <span className="text-muted fw-normal">(boş = süresiz)</span></label>
                <input type="datetime-local" className="form-control form-control-sm" value={form.endsAtUtc}
                  onChange={(e) => setForm((p) => ({ ...p, endsAtUtc: e.target.value }))} />
              </div>
              <div className="col-12">
                <label className="form-label small fw-semibold">Açıklama <span className="text-muted fw-normal">(opsiyonel)</span></label>
                <input type="text" className="form-control form-control-sm" maxLength={250} value={form.description}
                  placeholder="Örn. Hafta sonu indirimi" onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
              </div>
            </div>
            {formError && <div className="alert alert-danger py-2 px-3 mt-2 mb-0 small">{formError}</div>}
            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-sm btn-outline-secondary flex-grow-1" onClick={() => setShowForm(false)}>Vazgeç</button>
              <button className="btn btn-sm btn-warning text-white flex-grow-1" style={{ fontWeight: 600 }} onClick={handleSubmit} disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm" /> : (editingId ? "Güncelle" : "Kampanyayı Başlat")}
              </button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default FoodDiscountPopup;
